import { ComponentProps } from 'react';
import {
    Tabs as AriaTabs,
    Tab,
    TabList,
    TabPanel,
} from 'react-aria-components';
import styled from 'styled-components';
import { colors } from '../utils/colors';

export type TabItem = {
    label: React.ReactNode;
    content: React.ReactNode;
    name: string;
    ariaLabel: string;
};

type Props = {
    tabs: TabItem[];
    ariaLabel: string;
    defaultSelectedKey?: TabItem['name'];
} & (
    | { selectedKey?: never; onChange?: never }
    | {
          selectedKey: TabItem['name'];
          onChange: ComponentProps<typeof AriaTabs>['onSelectionChange'];
      }
);

export function Tabs({
    tabs,
    ariaLabel,
    defaultSelectedKey,
    onChange,
    selectedKey,
}: Props) {
    return (
        <AriaTabs
            defaultSelectedKey={defaultSelectedKey}
            selectedKey={selectedKey}
            onSelectionChange={onChange}
        >
            <StyledTabList aria-label={ariaLabel}>
                {tabs.map((v) => (
                    <StyledTab id={v.name} key={v.name}>
                        {v.label}
                    </StyledTab>
                ))}
            </StyledTabList>

            {tabs.map((v) => (
                <StyledTabPanel
                    aria-label={v.ariaLabel}
                    id={v.name}
                    key={v.name}
                >
                    {v.content}
                </StyledTabPanel>
            ))}
        </AriaTabs>
    );
}

const StyledTab = styled(Tab)`
    display: flex;
    align-items: center;
    border-radius: 16px 16px 0 0;
    padding: 16px;
    gap: 12px;
    font-size: 16px;
    color: ${colors.gray900};
    background-color: ${colors.blue50};
    border: none;
    cursor: pointer;

    &[data-selected] {
        color: ${colors.blue600};
        background-color: ${colors.white};
    }

    &:focus-visible {
        outline: 4px solid ${colors.indigo400};
    }

    &:hover {
        background-color: ${colors.blue200};
    }

    &:active {
        background-color: ${colors.blue200};
    }
`;

const StyledTabList = styled(TabList)`
    display: flex;
    width: fit-content;
    justify-content: flex-start;
    flex-wrap: nowrap;
    gap: 8px;
`;

const StyledTabPanel = styled(TabPanel)`
    display: block;
    border-top-left-radius: 0;
`;
